import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { TokenService } from '@/services/tokenService';
import { Building, Users, ArrowLeft, Mail, Lock, User, Briefcase, CheckCircle2, XCircle } from 'lucide-react';

interface OrganizationSignupProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const OrganizationSignup = ({ onBack, onSuccess }: OrganizationSignupProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState<'org-details' | 'admin-account'>('org-details');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [orgData, setOrgData] = useState({
    company_name: '',
    company_size: 'small',
    industry_sector: '',
    company_website: ''
  });

  const [adminData, setAdminData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    job_title: ''
  });

  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  const checkPasswordStrength = (password: string) => {
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const isPasswordStrong = () => Object.values(passwordStrength).every(v => v === true);

  // ─── SEND EMAIL HELPER ──────────────────────────────────────────────────────
  const sendEmail = async (to: string, subject: string, html: string) => {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: { to, subject, html },
    });
    if (error) throw new Error(`Failed to send email: ${error.message}`);
    if (data && !data.success) throw new Error(data.error || "Failed to send email");
    return data;
  };

  // ─── STEP 1: ORG DETAILS ─────────────────────────────────────────────────────
  const handleOrgSubmit = () => {
    if (!orgData.company_name) {
      toast({
        title: "Missing Information",
        description: "Please enter your organization name",
        variant: "destructive",
      });
      return;
    }
    setStep('admin-account');
  };

  // ─── STEP 2: CREATE ACCOUNT ──────────────────────────────────────────────────
  const handleSignup = async () => {
    if (!adminData.email || !adminData.password || !adminData.full_name) {
      toast({ title: "Missing Information", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminData.email)) {
      toast({ title: "Invalid Email", description: "Please enter a valid email address", variant: "destructive" });
      return;
    }

    if (adminData.password !== adminData.confirmPassword) {
      toast({ title: "Password Mismatch", description: "Passwords do not match", variant: "destructive" });
      return;
    }

    if (!isPasswordStrong()) {
      toast({ title: "Weak Password", description: "Please meet all password requirements", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // ── STEP 1: Create account via custom-signup edge function ──────────────
      // Pass org metadata via userMetadata so your DB trigger seeds the org correctly.
      // Your custom-signup function should forward userMetadata to user_metadata
      // in admin.createUser — check the note below if it doesn't yet.
      const { data: signUpData, error: signUpError } = await supabase.functions.invoke("custom-signup", {
        body: {
          email: adminData.email.toLowerCase(),
          password: adminData.password,
          fullName: adminData.full_name,
          userMetadata: {
            full_name: adminData.full_name,
            job_title: adminData.job_title,
            company_name: orgData.company_name,
            company_size: orgData.company_size,
            industry_sector: orgData.industry_sector,
            company_website: orgData.company_website,
            role: 'admin', // DB trigger reads this to seed the organization
          }
        },
      });

      if (signUpError) {
        let errorMessage = "An unexpected error occurred";
        try {
          const errorBody = await signUpError.context.json();
          errorMessage = errorBody.error || signUpError.message;
        } catch {
          errorMessage = signUpError.message;
        }
        throw new Error(errorMessage);
      }

      if (signUpData?.error) throw new Error(signUpData.error);

      // ── STEP 2: Generate email verification token ───────────────────────────
      const { success, token, error: tokenError } = await TokenService.createEmailVerificationToken(
        adminData.email.toLowerCase()
      );

      if (!success || !token) {
        console.error("Token creation failed:", tokenError);
        toast({
          title: "Organization Created!",
          description: "Your account was created. Please check your email to verify.",
          duration: 8000,
        });
        onBack();
        return;
      }

      // ── STEP 3: Build verification link ─────────────────────────────────────
      // No &org= param here — admin is the org creator, not a team invitee
      const verificationLink = `${window.location.origin}/verify-email?token=${token}`;

      // ── STEP 4: Send branded verification email ─────────────────────────────
      await sendEmail(
        adminData.email.toLowerCase(),
        "Verify your GreenCard organization account",
        `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
              <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
            </div>

            <h2 style="color: #1a1a1a;">Welcome, ${adminData.full_name}! 🌱</h2>

            <p style="color: #444; line-height: 1.6;">
              Your organization <strong>${orgData.company_name}</strong> has been set up.
              Please verify your email address to activate your admin account.
            </p>

            <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px; margin: 20px 0;">
              <p style="margin: 0; color: #15803d; font-size: 14px; line-height: 1.8;">
                <strong>Organization:</strong> ${orgData.company_name}<br/>
                ${orgData.industry_sector ? `<strong>Industry:</strong> ${orgData.industry_sector}<br/>` : ''}
                <strong>Your Role:</strong> Administrator
              </p>
            </div>

            <div style="text-align: center; margin: 30px 0;">
              <a href="${verificationLink}"
                style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
                       text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
                Verify Email & Activate Account
              </a>
            </div>

            <p style="color: #666; font-size: 14px; line-height: 1.6;">
              This link will expire in <strong>24 hours</strong>.
              If you didn't create this account, you can safely ignore this email.
            </p>

            <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
            <p style="color: #999; font-size: 12px; text-align: center;">
              GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
              <a href="${verificationLink}" style="color: #16a34a;">${verificationLink}</a>
            </p>
          </div>
        `
      );

      toast({
        title: "✅ Organization Created!",
        description: "We've sent a verification link to your email. Click it to activate your admin account.",
        duration: 8000,
      });

      onBack();

    } catch (error: any) {
      console.error('Organization signup error:', error);
      toast({
        title: "Signup Failed",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── PASSWORD STRENGTH INDICATOR ─────────────────────────────────────────────
  const PasswordStrengthIndicator = () => (
    <div className="space-y-1 mt-2 text-xs">
      {[
        { key: 'hasMinLength', label: 'At least 8 characters' },
        { key: 'hasUpperCase', label: 'One uppercase letter' },
        { key: 'hasLowerCase', label: 'One lowercase letter' },
        { key: 'hasNumber', label: 'One number' },
        { key: 'hasSpecialChar', label: 'One special character' },
      ].map(({ key, label }) => (
        <div key={key} className="flex items-center gap-2">
          {passwordStrength[key as keyof typeof passwordStrength]
            ? <CheckCircle2 className="h-3 w-3 text-green-600" />
            : <XCircle className="h-3 w-3 text-gray-400" />
          }
          <span className={passwordStrength[key as keyof typeof passwordStrength] ? "text-green-600" : "text-gray-600"}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );

  // ─── STEP 1 RENDER ───────────────────────────────────────────────────────────
  if (step === 'org-details') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
            <div>
              <CardTitle className="flex items-center gap-2"><Building className="h-6 w-6" />Create Your Organization</CardTitle>
              <p className="text-sm text-muted-foreground">Step 1: Organization Details</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="company_name">Organization Name *</Label>
            <Input id="company_name" value={orgData.company_name} onChange={(e) => setOrgData(prev => ({ ...prev, company_name: e.target.value }))} placeholder="Enter your organization name" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="company_size">Company Size</Label>
            <Select value={orgData.company_size} onValueChange={(value) => setOrgData(prev => ({ ...prev, company_size: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small (1–50 employees)</SelectItem>
                <SelectItem value="medium">Medium (51–250 employees)</SelectItem>
                <SelectItem value="large">Large (251–1000 employees)</SelectItem>
                <SelectItem value="enterprise">Enterprise (1000+ employees)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="industry_sector">Industry Sector</Label>
            <Input id="industry_sector" value={orgData.industry_sector} onChange={(e) => setOrgData(prev => ({ ...prev, industry_sector: e.target.value }))} placeholder="e.g., Technology, Manufacturing, Healthcare" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="company_website">Company Website</Label>
            <Input id="company_website" type="url" value={orgData.company_website} onChange={(e) => setOrgData(prev => ({ ...prev, company_website: e.target.value }))} placeholder="https://www.company.com" className="mt-1" />
          </div>
          <Alert>
            <Users className="h-4 w-4" />
            <AlertDescription>As an organization admin, you'll be able to invite team members and manage shared data and reports.</AlertDescription>
          </Alert>
          <Button onClick={handleOrgSubmit} className="w-full">Continue to Admin Account</Button>
        </CardContent>
      </Card>
    );
  }

  // ─── STEP 2 RENDER ───────────────────────────────────────────────────────────
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setStep('org-details')}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <CardTitle>Create Admin Account</CardTitle>
            <p className="text-sm text-muted-foreground">Step 2: Admin Account for <span className="font-semibold">{orgData.company_name}</span></p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        <div>
          <Label htmlFor="adminName" className="flex items-center gap-2 mb-1"><User className="h-4 w-4" />Full Name *</Label>
          <Input id="adminName" value={adminData.full_name} onChange={(e) => setAdminData(prev => ({ ...prev, full_name: e.target.value }))} placeholder="Enter your full name" />
        </div>

        <div>
          <Label htmlFor="adminEmail" className="flex items-center gap-2 mb-1"><Mail className="h-4 w-4" />Admin Email *</Label>
          <Input id="adminEmail" type="email" value={adminData.email} onChange={(e) => setAdminData(prev => ({ ...prev, email: e.target.value }))} placeholder="admin@company.com" />
        </div>

        <div>
          <Label htmlFor="adminTitle" className="flex items-center gap-2 mb-1"><Briefcase className="h-4 w-4" />Job Title</Label>
          <Input id="adminTitle" value={adminData.job_title} onChange={(e) => setAdminData(prev => ({ ...prev, job_title: e.target.value }))} placeholder="e.g., Sustainability Manager" />
        </div>

        <div>
          <Label htmlFor="adminPassword" className="flex items-center gap-2 mb-1"><Lock className="h-4 w-4" />Password *</Label>
          <div className="relative">
            <Input
              id="adminPassword"
              type={showPassword ? "text" : "password"}
              value={adminData.password}
              onChange={(e) => { setAdminData(prev => ({ ...prev, password: e.target.value })); checkPasswordStrength(e.target.value); }}
              placeholder="Create a strong password"
            />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {adminData.password && <PasswordStrengthIndicator />}
        </div>

        <div>
          <Label htmlFor="confirmPassword" className="flex items-center gap-2 mb-1"><Lock className="h-4 w-4" />Confirm Password *</Label>
          <Input id="confirmPassword" type="password" value={adminData.confirmPassword} onChange={(e) => setAdminData(prev => ({ ...prev, confirmPassword: e.target.value }))} placeholder="Confirm your password" />
          {adminData.confirmPassword && adminData.password !== adminData.confirmPassword && (
            <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
          )}
        </div>

        <Alert>
          <AlertDescription>
            <p className="font-medium mb-1">📋 What happens next:</p>
            <ul className="text-sm space-y-1">
              <li>• Your admin account will be created</li>
              <li>• You'll receive a verification email from us</li>
              <li>• Click the link to activate your account</li>
              <li>• Sign in to access your organization dashboard</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Button
          onClick={handleSignup}
          disabled={loading || !isPasswordStrong() || adminData.password !== adminData.confirmPassword}
          className="w-full bg-green-600 hover:bg-green-700"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating Organization...
            </span>
          ) : 'Create Organization & Account'}
        </Button>

      </CardContent>
    </Card>
  );
};






// import React, { useState } from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { useToast } from '@/hooks/use-toast';
// import { supabase } from '@/integrations/supabase/client';
// import { Building, Users, ArrowLeft, Mail, Lock, User, Briefcase } from 'lucide-react';

// interface OrganizationSignupProps {
//   onBack: () => void;
//   onSuccess: () => void;
// }

// export const OrganizationSignup = ({ onBack, onSuccess }: OrganizationSignupProps) => {
//   const { toast } = useToast();
//   const [step, setStep] = useState<'org-details' | 'admin-account'>('org-details');
//   const [loading, setLoading] = useState(false);
  
//   const [orgData, setOrgData] = useState({
//     company_name: '',
//     company_size: 'small',
//     industry_sector: '',
//     company_website: ''
//   });
  
//   const [adminData, setAdminData] = useState({
//     email: '',
//     password: '',
//     full_name: '',
//     job_title: ''
//   });

//   const handleOrgSubmit = () => {
//     if (!orgData.company_name) {
//       toast({
//         title: "Missing Information",
//         description: "Please enter your organization name",
//         variant: "destructive",
//       });
//       return;
//     }
//     setStep('admin-account');
//   };

//   const handleSignup = async () => {
//     if (!adminData.email || !adminData.password || !adminData.full_name) {
//       toast({
//         title: "Missing Information",
//         description: "Please fill in all required admin account fields",
//         variant: "destructive"
//       });
//       return;
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!emailRegex.test(adminData.email)) {
//       toast({
//         title: "Invalid Email",
//         description: "Please enter a valid email address",
//         variant: "destructive",
//       });
//       return;
//     }

//     if (adminData.password.length < 8) {
//       toast({
//         title: "Weak Password",
//         description: "Password must be at least 8 characters long",
//         variant: "destructive",
//       });
//       return;
//     }

//     setLoading(true);
    
//     try {
//       const { data: authData, error: authError } = await supabase.auth.signUp({
//         email: adminData.email.toLowerCase(),
//         password: adminData.password,
//         options: {
//           emailRedirectTo: `${window.location.origin}/`,
//           data: {
//             full_name: adminData.full_name,
//             job_title: adminData.job_title,
//             company_name: orgData.company_name,
//             company_size: orgData.company_size,
//             industry_sector: orgData.industry_sector,
//             company_website: orgData.company_website,
//             role: 'admin' // Trigger detects this to seed the organization
//           }
//         }
//       });

//       if (authError) {
//         if (authError.message.includes('already registered')) {
//           throw new Error('An account with this email already exists. Please sign in instead.');
//         }
//         throw authError;
//       }

//       if (!authData.user) {
//         throw new Error('Failed to create user account');
//       }

//       // Success!
//       toast({
//         title: "✅ Organization Created!",
//         description: "Please check your email to verify your account. Once verified, you can log in as the administrator.",
//         duration: 8000,
//       });

//       // Navigate back to login or home
//       onBack();
      
//     } catch (error: any) {      
//       console.error('Signup process error:', error);
      
//       toast({
//         title: "Signup Failed",
//         description: error.message || "An unexpected error occurred.",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (step === 'org-details') {
//     return (
//       <Card className="w-full max-w-md mx-auto">
//         <CardHeader>
//           <div className="flex items-center gap-2">
//             <Button variant="ghost" size="sm" onClick={onBack}>
//               <ArrowLeft className="h-4 w-4" />
//             </Button>
//             <div>
//               <CardTitle className="flex items-center gap-2">
//                 <Building className="h-6 w-6" />
//                 Create Your Organization
//               </CardTitle>
//               <p className="text-sm text-muted-foreground">Step 1: Organization Details</p>
//             </div>
//           </div>
//         </CardHeader>
//         <CardContent className="space-y-4">
//           <div>
//             <Label htmlFor="company_name">Organization Name *</Label>
//             <Input
//               id="company_name"
//               value={orgData.company_name}
//               onChange={(e) => setOrgData(prev => ({ ...prev, company_name: e.target.value }))}
//               placeholder="Enter your organization name"
//               className="mt-1"
//             />
//           </div>

//           <div>
//             <Label htmlFor="company_size">Company Size</Label>
//             <Select 
//               value={orgData.company_size} 
//               onValueChange={(value) => setOrgData(prev => ({ ...prev, company_size: value }))}
//             >
//               <SelectTrigger>
//                 <SelectValue placeholder="Select company size" />
//               </SelectTrigger>
//               <SelectContent>
//                 <SelectItem value="small">Small (1-50 employees)</SelectItem>
//                 <SelectItem value="medium">Medium (51-250 employees)</SelectItem>
//                 <SelectItem value="large">Large (251-1000 employees)</SelectItem>
//                 <SelectItem value="enterprise">Enterprise (1000+ employees)</SelectItem>
//               </SelectContent>
//             </Select>
//           </div>

//           <div>
//             <Label htmlFor="industry_sector">Industry Sector</Label>
//             <Input
//               id="industry_sector"
//               value={orgData.industry_sector}
//               onChange={(e) => setOrgData(prev => ({ ...prev, industry_sector: e.target.value }))}
//               placeholder="e.g., Technology, Manufacturing, Healthcare"
//               className="mt-1"
//             />
//           </div>

//           <div>
//             <Label htmlFor="company_website">Company Website</Label>
//             <Input
//               id="company_website"
//               type="url"
//               value={orgData.company_website}
//               onChange={(e) => setOrgData(prev => ({ ...prev, company_website: e.target.value }))}
//               placeholder="https://www.company.com"
//               className="mt-1"
//             />
//           </div>

//           <Alert>
//             <Users className="h-4 w-4" />
//             <AlertDescription>
//               As an organization admin, you'll be able to invite team members and manage shared data and reports.
//             </AlertDescription>
//           </Alert>

//           <Button onClick={handleOrgSubmit} className="w-full">
//             Continue to Admin Account
//           </Button>
//         </CardContent>
//       </Card>
//     );
//   }

//   return (
//     <Card className="w-full max-w-md mx-auto">
//       <CardHeader>
//         <div className="flex items-center gap-2">
//           <Button variant="ghost" size="sm" onClick={() => setStep('org-details')}>
//             <ArrowLeft className="h-4 w-4" />
//           </Button>
//           <div>
//             <CardTitle>Create Admin Account</CardTitle>
//             <p className="text-sm text-muted-foreground">
//               Step 2: Admin Account for <span className="font-semibold">{orgData.company_name}</span>
//             </p>
//           </div>
//         </div>
//       </CardHeader>
//       <CardContent className="space-y-4">
//         <div>
//           <Label htmlFor="adminEmail" className="flex items-center gap-2 mb-2">
//             <Mail className="h-4 w-4" />
//             Admin Email *
//           </Label>
//           <Input
//             id="adminEmail"
//             type="email"
//             value={adminData.email}
//             onChange={(e) => setAdminData(prev => ({ ...prev, email: e.target.value }))}
//             placeholder="admin@company.com"
//             className="mt-1"
//           />
//         </div>

//         <div>
//           <Label htmlFor="adminPassword" className="flex items-center gap-2 mb-2">
//             <Lock className="h-4 w-4" />
//             Password *
//           </Label>
//           <Input
//             id="adminPassword"
//             type="password"
//             value={adminData.password}
//             onChange={(e) => setAdminData(prev => ({ ...prev, password: e.target.value }))}
//             placeholder="At least 8 characters"
//             className="mt-1"
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             Use a strong password with letters, numbers, and symbols
//           </p>
//         </div>

//         <div>
//           <Label htmlFor="adminName" className="flex items-center gap-2 mb-2">
//             <User className="h-4 w-4" />
//             Full Name *
//           </Label>
//           <Input
//             id="adminName"
//             value={adminData.full_name}
//             onChange={(e) => setAdminData(prev => ({ ...prev, full_name: e.target.value }))}
//             placeholder="Enter your full name"
//             className="mt-1"
//           />
//         </div>

//         <div>
//           <Label htmlFor="adminTitle" className="flex items-center gap-2 mb-2">
//             <Briefcase className="h-4 w-4" />
//             Job Title
//           </Label>
//           <Input
//             id="adminTitle"
//             value={adminData.job_title}
//             onChange={(e) => setAdminData(prev => ({ ...prev, job_title: e.target.value }))}
//             placeholder="e.g., Sustainability Manager"
//             className="mt-1"
//           />
//         </div>

//         <Alert>
//           <AlertDescription>
//             <div className="space-y-2">
//               <p className="font-medium">📋 What happens next:</p>
//               <ul className="text-sm space-y-1">
//                 <li>• Your admin account will be created</li>
//                 <li>• Check your email for verification link</li>
//                 <li>• Click the link to verify your account</li>
//                 <li>• Sign in to access your organization dashboard</li>
//               </ul>
//             </div>
//           </AlertDescription>
//         </Alert>

//         <Button 
//           onClick={handleSignup} 
//           disabled={loading} 
//           className="w-full bg-green-600 hover:bg-green-700"
//         >
//           {loading ? (
//             <>
//               <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
//               Creating Organization...
//             </>
//           ) : (
//             'Create Organization & Account'
//           )}
//         </Button>
//       </CardContent>
//     </Card>
//   );
// };






// import React, { useState } from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { useToast } from '@/hooks/use-toast';
// import { supabase } from '@/integrations/supabase/client';
// import { Building, Users, ArrowLeft } from 'lucide-react';

// interface OrganizationSignupProps {
//   onBack: () => void;
//   onSuccess: () => void;
// }

// export const OrganizationSignup = ({ onBack, onSuccess }: OrganizationSignupProps) => {
//   const { toast } = useToast();
//   const [step, setStep] = useState<'org-details' | 'admin-account'>('org-details');
//   const [loading, setLoading] = useState(false);
  
//   const [orgData, setOrgData] = useState({
//     name: '',
//     teamSize: '',
//     industry: ''
//   });
  
//   const [adminData, setAdminData] = useState({
//     email: '',
//     password: '',
//     fullName: '',
//     jobTitle: ''
//   });

//   const handleOrgSubmit = () => {
//     if (!orgData.name || !orgData.teamSize) {
//       toast({
//         title: "Missing Information",
//         description: "Please fill in all required fields",
//         variant: "destructive",
//       });
//       return;
//     }
//     setStep('admin-account');
//   };

//   const handleSignup = async () => {
//     if (!adminData.email || !adminData.password || !adminData.fullName) {
//       toast({
//         title: "Missing Information",
//         description: "Please fill in all required admin account fields",
//         variant: "destructive",
//       });
//       return;
//     }

//     setLoading(true);
    
//     try {
//       // Create admin account
//       const { data: authData, error: authError } = await supabase.auth.signUp({
//         email: adminData.email,
//         password: adminData.password,
//         options: {
//           emailRedirectTo: `${window.location.origin}/`,
//           data: {
//             full_name: adminData.fullName,
//             job_title: adminData.jobTitle
//           }
//         }
//       });

//       if (authError) throw authError;

//       if (authData.user) {
//         // Create organization
//         const { data: orgResult, error: orgError } = await supabase
//           .from('organizations')
//           .insert({
//             organization_name: orgData.name,
//             admin_user_id: authData.user.id,
//             storage_config: {
//               team_size: orgData.teamSize,
//               industry: orgData.industry
//             }
//           })
//           .select()
//           .single();

//         if (orgError) throw orgError;

//         // Create user profile
//         await supabase
//           .from('user_profiles')
//           .insert({
//             user_id: authData.user.id,
//             full_name: adminData.fullName,
//             job_title: adminData.jobTitle,
//             company_name: orgData.name,
//             organization_id: orgResult.id,
//             role: 'admin',
//             requires_storage_setup: true
//           });

//         toast({
//           title: "Organization Created!",
//           description: "Your organization has been created successfully. Please check your email to verify your account.",
//         });

//         onSuccess();
//       }
//     } catch (error) {
//       console.log('Signup error:', error);
//       toast({
//         title: "Signup Failed",
//         description: error instanceof Error ? error.message : "Failed to create organization",
//         variant: "destructive",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (step === 'org-details') {
//     return (
//       <Card className="w-full max-w-md mx-auto">
//         <CardHeader>
//           <div className="flex items-center gap-2">
//             <Button variant="ghost" size="sm" onClick={onBack}>
//               <ArrowLeft className="h-4 w-4" />
//             </Button>
//             <div>
//               <CardTitle className="flex items-center gap-2">
//                 <Building className="h-6 w-6" />
//                 Organization Signup
//               </CardTitle>
//               <p className="text-sm text-muted-foreground">Step 1: Organization Details</p>
//             </div>
//           </div>
//         </CardHeader>
//         <CardContent className="space-y-4">
//           <div>
//             <Label htmlFor="orgName">Organization Name *</Label>
//             <Input
//               id="orgName"
//               value={orgData.name}
//               onChange={(e) => setOrgData(prev => ({ ...prev, name: e.target.value }))}
//               placeholder="Enter your organization name"
//             />
//           </div>

//           <div>
//             <Label htmlFor="teamSize">Expected Team Size *</Label>
//             <Select value={orgData.teamSize} onValueChange={(value) => setOrgData(prev => ({ ...prev, teamSize: value }))}>
//               <SelectTrigger>
//                 <SelectValue placeholder="Select team size" />
//               </SelectTrigger>
//               <SelectContent>
//                 <SelectItem value="2-5">2-5 members</SelectItem>
//                 <SelectItem value="6-15">6-15 members</SelectItem>
//                 <SelectItem value="16-50">16-50 members</SelectItem>
//                 <SelectItem value="51-200">51-200 members</SelectItem>
//                 <SelectItem value="200+">200+ members</SelectItem>
//               </SelectContent>
//             </Select>
//           </div>

//           <div>
//             <Label htmlFor="industry">Industry (Optional)</Label>
//             <Input
//               id="industry"
//               value={orgData.industry}
//               onChange={(e) => setOrgData(prev => ({ ...prev, industry: e.target.value }))}
//               placeholder="e.g., Manufacturing, Finance, Healthcare"
//             />
//           </div>

//           <Alert>
//             <Users className="h-4 w-4" />
//             <AlertDescription>
//               As an organization admin, you'll be able to invite team members and manage shared storage after signup.
//             </AlertDescription>
//           </Alert>

//           <Button onClick={handleOrgSubmit} className="w-full">
//             Continue to Admin Account
//           </Button>
//         </CardContent>
//       </Card>
//     );
//   }

//   return (
//     <Card className="w-full max-w-md mx-auto">
//       <CardHeader>
//         <div className="flex items-center gap-2">
//           <Button variant="ghost" size="sm" onClick={() => setStep('org-details')}>
//             <ArrowLeft className="h-4 w-4" />
//           </Button>
//           <div>
//             <CardTitle>Create Admin Account</CardTitle>
//             <p className="text-sm text-muted-foreground">Step 2: Admin Account for {orgData.name}</p>
//           </div>
//         </div>
//       </CardHeader>
//       <CardContent className="space-y-4">
//         <div>
//           <Label htmlFor="adminEmail">Admin Email *</Label>
//           <Input
//             id="adminEmail"
//             type="email"
//             value={adminData.email}
//             onChange={(e) => setAdminData(prev => ({ ...prev, email: e.target.value }))}
//             placeholder="admin@company.com"
//           />
//         </div>

//         <div>
//           <Label htmlFor="adminPassword">Password *</Label>
//           <Input
//             id="adminPassword"
//             type="password"
//             value={adminData.password}
//             onChange={(e) => setAdminData(prev => ({ ...prev, password: e.target.value }))}
//             placeholder="Create a secure password"
//           />
//         </div>

//         <div>
//           <Label htmlFor="adminName">Full Name *</Label>
//           <Input
//             id="adminName"
//             value={adminData.fullName}
//             onChange={(e) => setAdminData(prev => ({ ...prev, fullName: e.target.value }))}
//             placeholder="Enter your full name"
//           />
//         </div>

//         <div>
//           <Label htmlFor="adminTitle">Job Title</Label>
//           <Input
//             id="adminTitle"
//             value={adminData.jobTitle}
//             onChange={(e) => setAdminData(prev => ({ ...prev, jobTitle: e.target.value }))}
//             placeholder="e.g., Sustainability Manager"
//           />
//         </div>

//         <Alert>
//           <AlertDescription>
//             After creating your account, you'll be prompted to set up your organization's shared storage location.
//           </AlertDescription>
//         </Alert>

//         <Button onClick={handleSignup} disabled={loading} className="w-full">
//           {loading ? 'Creating Organization...' : 'Create Organization'}
//         </Button>
//       </CardContent>
//     </Card>
//   );
// };
